import { useEffect, useMemo, useRef, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { apiFetch as api } from "utils/api";

function isImage(mime) {
  return (mime || "").startsWith("image/");
}

export default function AdminScreens() {

  const [customers, setCustomers] = useState([]);
  const [screens, setScreens] = useState([]);
  const [customerId, setCustomerId] = useState("ALL");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Pairing dialog state (Admin erstellt Code, Device claimt dann)
  const [pairOpen, setPairOpen] = useState(false);
  const [pairCustomerId, setPairCustomerId] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [pairingCreating, setPairingCreating] = useState(false);

  // NEU:
  const [pairingStatus, setPairingStatus] = useState("");
  const pairingPollRef = useRef(null);

  // Loop editor dialog state
  const [loopOpen, setLoopOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState(null);
  const [loopItems, setLoopItems] = useState([]);
  const [customerMedia, setCustomerMedia] = useState([]);
  const [mediaToAdd, setMediaToAdd] = useState("");
  const [loopLoading, setLoopLoading] = useState(false);
  const [loopErr, setLoopErr] = useState("");
  const [savingLoop, setSavingLoop] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [savingScreen, setSavingScreen] = useState(false);
  const [deletingScreenId, setDeletingScreenId] = useState("");

  const filtered = useMemo(() => {
    if (customerId === "ALL") return screens;
    return screens.filter((s) => s.customerId === customerId || s.customer?.id === customerId);
  }, [screens, customerId]);

  const loadCustomersAndScreens = async () => {
    setLoading(true);
    setErr("");
    try {
      const [cData, sData] = await Promise.all([
        api("/api/admin/customers"),
        api("/api/admin/screens"),
      ]);
      setCustomers(Array.isArray(cData) ? cData : cData?.customers || []);
      setScreens(Array.isArray(sData) ? sData : sData?.screens || []);
    } catch (e) {
      setErr(e?.message || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomersAndScreens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const openPairing = () => {
    setPairCustomerId(customerId !== "ALL" ? customerId : "");
    setPairingCode("");
    setPairingStatus("");
    setErr("");

    if (pairingPollRef.current) {
      clearInterval(pairingPollRef.current);
      pairingPollRef.current = null;
    }

    setPairOpen(true);
  };

  const closePairing = () => {
    setPairOpen(false);
    if (pairingPollRef.current) {
      clearInterval(pairingPollRef.current);
      pairingPollRef.current = null;
    }
  };

  const startPairingPoll = (code) => {
    if (!code) return;

    if (pairingPollRef.current) {
      clearInterval(pairingPollRef.current);
      pairingPollRef.current = null;
    }

    setPairingStatus("Warte auf Gerät…");

    pairingPollRef.current = setInterval(async () => {
      try {
        const p = await api(`/api/pairings/${encodeURIComponent(code)}`);
        if (p?.screenId) {
          setPairingStatus("Gekoppelt ✅");
          await loadCustomersAndScreens();

          if (pairingPollRef.current) {
            clearInterval(pairingPollRef.current);
            pairingPollRef.current = null;
          }
        }
      } catch {
        // ignorieren während wartend
      }
    }, 2000);
  };

    const createPairing = async () => {
    setErr("");
    if (!pairCustomerId) return setErr("Bitte einen Kunden auswählen.");

    setPairingCreating(true);
    try {
      const created = await api("/api/pairings", {
        method: "POST",
        body: { customerId: pairCustomerId },
      });

      const code = String(created?.code || "").trim();
      setPairingCode(code);

      if (code) startPairingPoll(code);
      else {
        setPairingStatus("");
        setErr("Backend hat keinen Code zurückgegeben.");
      }
    } catch (e) {
      setErr(e?.message || "Pairing-Code konnte nicht erstellt werden.");
    } finally {
      setPairingCreating(false);
    }
  };

  const openLoopEditor = async (screen) => {
    setActiveScreen(screen);
    setLoopItems([]);
    setCustomerMedia([]);
    setMediaToAdd("");
    setLoopErr("");
    setLoopLoading(true);
    setLoopOpen(true);

    try {
      // 1) load loop items
      const loopData = await api(`/api/admin/screens/${screen.id}/loop`);
      const items = Array.isArray(loopData) ? loopData : loopData?.items || loopData?.loopItems || [];
      // normalize sort by position
      items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setLoopItems(items);

      // 2) load customer media for add dropdown
      const cid = screen.customerId || screen.customer?.id;
      if (cid) {
        const mediaData = await api(`/api/admin/media?customerId=${cid}`);
        const media = Array.isArray(mediaData) ? mediaData : mediaData?.media || mediaData?.items || [];
        setCustomerMedia(media);
      }
    } catch (e) {
      setLoopErr(
        e?.message ||
          "Loop konnte nicht geladen werden. (Backend-Endpunkt fehlt evtl.: /api/admin/screens/:id/loop)"
      );
    } finally {
      setLoopLoading(false);
    }
  };

  const moveItem = (idx, dir) => {
    const next = [...loopItems];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[target];
    next[target] = tmp;

    // re-assign positions 0..n-1
    const normalized = next.map((it, i) => ({ ...it, position: i }));
    setLoopItems(normalized);
  };

  const setDuration = (idx, value) => {
    const v = value === "" ? "" : Number(value);
    const next = [...loopItems];
    next[idx] = { ...next[idx], durationSeconds: v === "" ? null : Math.max(1, Number.isFinite(v) ? v : 5) };
    setLoopItems(next);
  };

  const removeItem = (idx) => {
    const next = loopItems.filter((_, i) => i !== idx).map((it, i) => ({ ...it, position: i }));
    setLoopItems(next);
  };

  const addSelectedMedia = () => {
    if (!mediaToAdd) return;
    const media = customerMedia.find((m) => m.id === mediaToAdd);
    if (!media) return;

    // prevent duplicates? allow duplicates is okay, but for now avoid accidental dupes by default:
    const exists = loopItems.some((li) => li.mediaId === media.id);
    if (exists) return;

    const pos = loopItems.length;
    const newItem = {
      id: `tmp-${media.id}`,
      screenId: activeScreen?.id,
      mediaId: media.id,
      media,
      position: pos,
      durationSeconds: isImage(media.mimeType) ? 5 : null,
    };

    setLoopItems([...loopItems, newItem]);
    setMediaToAdd("");
  };

  const saveLoop = async () => {
    if (!activeScreen?.id) return;
    setSavingLoop(true);
    setLoopErr("");

    try {
      const payload = {
        items: loopItems.map((it, idx) => ({
          mediaId: it.mediaId || it.media?.id,
          position: idx,
          durationSeconds: it.durationSeconds ?? null,
        })),
      };

      await api(`/api/admin/screens/${activeScreen.id}/loop`, {
        method: "PUT",
        body: payload,
      });

      // reload screens list (optional)
      await loadCustomersAndScreens();
      setLoopOpen(false);
    } catch (e) {
      setLoopErr(
        e?.message ||
          "Speichern fehlgeschlagen. (Backend-Endpunkt fehlt evtl.: PUT /api/admin/screens/:id/loop)"
      );
    } finally {
      setSavingLoop(false);
    }
  };

  const openEditScreen = (screen) => {
    setEditingScreen(screen);
    setEditName(screen?.name || "");
    setEditLocation(screen?.location || "");
    setEditOpen(true);
  };

  const closeEditScreen = () => {
    setEditOpen(false);
    setEditingScreen(null);
    setEditName("");
    setEditLocation("");
  };

  const saveScreenMeta = async () => {
    if (!editingScreen?.id) return;
    if (!String(editName || "").trim()) return setErr("Name darf nicht leer sein.");

    setSavingScreen(true);
    setErr("");
    try {
      await api(`/api/admin/screens/${editingScreen.id}`, {
        method: "PATCH",
        body: {
          name: String(editName).trim(),
          location: String(editLocation || "").trim() || null,
        },
      });
      await loadCustomersAndScreens();
      closeEditScreen();
    } catch (e) {
      setErr(e?.message || "Bildschirm konnte nicht gespeichert werden.");
    } finally {
      setSavingScreen(false);
    }
  };

  const deleteScreen = async (screen) => {
    if (!screen?.id) return;
    const ok = window.confirm(`Bildschirm "${screen.name}" wirklich löschen?`);
    if (!ok) return;

    setDeletingScreenId(screen.id);
    setErr("");
    try {
      await api(`/api/admin/screens/${screen.id}`, { method: "DELETE" });
      await loadCustomersAndScreens();
      if (activeScreen?.id === screen.id) {
        setLoopOpen(false);
        setActiveScreen(null);
      }
    } catch (e) {
      setErr(e?.message || "Bildschirm konnte nicht gelöscht werden.");
    } finally {
      setDeletingScreenId("");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h5" fontWeight="medium">
          Bildschirme
        </MDTypography>

        <MDButton variant="gradient" color="info" size="small" onClick={openPairing}>
          Neu
        </MDButton>
      </MDBox>

      {!!err && (
        <MDBox mb={2}>
          <MDTypography variant="button" color="error">
            {err}
          </MDTypography>
        </MDBox>
      )}

      <Card>
        <MDBox p={2}>
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Kunde"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <MenuItem value="ALL">Alle</MenuItem>
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.customerNumber} – {c.companyName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {loading ? (
            <MDTypography variant="button" color="text">
              Lädt…
            </MDTypography>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900, width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 280, whiteSpace: "nowrap" }}>Bildschirm</TableCell>
                    <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>Kunde</TableCell>
                    <TableCell sx={{ width: 220, whiteSpace: "nowrap" }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow
                      key={s.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openLoopEditor(s)}
                    >
                      <TableCell
                        sx={{
                          width: 280,
                          maxWidth: 280,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 140,
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.customer?.customerNumber || s.customerNumber || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 220, whiteSpace: "nowrap" }}>
                        <MDBox display="flex" gap={1}>
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditScreen(s);
                            }}
                          >
                            Bearbeiten
                          </MDButton>
                          <MDButton
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={deletingScreenId === s.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteScreen(s);
                            }}
                          >
                            {deletingScreenId === s.id ? "Löscht…" : "Löschen"}
                          </MDButton>
                        </MDBox>
                      </TableCell>
                    </TableRow>
                  ))}

                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>Keine Bildschirme gefunden.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      </Card>

      {/* Pairing Dialog (Admin erstellt Code) */}
      <Dialog open={pairOpen} onClose={closePairing} fullWidth maxWidth="sm">
        <DialogTitle>Bildschirm koppeln (Pairing-Code)</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <TextField
              fullWidth
              select
              label="Kunde"
              value={pairCustomerId}
              onChange={(e) => setPairCustomerId(e.target.value)}
              margin="dense"
              disabled={!!pairingCode}
            >
              <MenuItem value="">Bitte wählen</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.customerNumber} – {c.companyName}
                </MenuItem>
              ))}
            </TextField>

            {!!pairingCode && (
  <MDBox mt={2}>
    <MDTypography variant="button" color="text">
      Pairing-Code (im Player eingeben):
    </MDTypography>

    <MDBox
      mt={1}
      p={2}
      borderRadius={2}
      sx={{
        border: "1px solid rgba(0,0,0,0.12)",
        textAlign: "center",
      }}
    >
      <MDTypography
        variant="h3"
        fontWeight="bold"
        sx={{ letterSpacing: 4 }}
      >
        {pairingCode}
      </MDTypography>
    </MDBox>

    <MDTypography
      variant="caption"
      color="text"
      display="block"
      mt={1}
    >
      Der Bildschirm wird automatisch erstellt, sobald das Gerät den Code eingibt.
    </MDTypography>

    {!!pairingStatus && (
      <MDTypography
        variant="button"
        color={pairingStatus.includes("Gekoppelt") ? "success" : "text"}
        display="block"
        mt={1}
      >
        Status: {pairingStatus}
      </MDTypography>
    )}
  </MDBox>
)}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={closePairing}>
           Schließen
          </MDButton>
          {!pairingCode ? (
            <MDButton
              variant="gradient"
              color="info"
              onClick={createPairing}
              disabled={pairingCreating}
            >
              {pairingCreating ? "Erstellt…" : "Code erstellen"}
            </MDButton>
          ) : (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(pairingCode);
                } catch (e) {
                  // ignore
                }
              }}
            >
              Code kopieren
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEditScreen} fullWidth maxWidth="sm">
        <DialogTitle>Bildschirm bearbeiten</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <TextField
              fullWidth
              label="Name"
              margin="dense"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <TextField
              fullWidth
              label="Ort"
              margin="dense"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={closeEditScreen}>
            Abbrechen
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={saveScreenMeta}
            disabled={savingScreen}
          >
            {savingScreen ? "Speichert…" : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Loop Editor Dialog */}
      <Dialog open={loopOpen} onClose={() => setLoopOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Loop bearbeiten{activeScreen?.name ? `: ${activeScreen.name}` : ""}
        </DialogTitle>

        <DialogContent>
          {loopLoading ? (
            <MDTypography variant="button" color="text">
              Lädt…
            </MDTypography>
          ) : (
            <>
              {loopErr && (
                <MDBox mb={1}>
                  <MDTypography variant="button" color="error">
                    {loopErr}
                  </MDTypography>
                </MDBox>
              )}

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    select
                    label="Medien hinzufügen"
                    value={mediaToAdd}
                    onChange={(e) => setMediaToAdd(e.target.value)}
                  >
                    <MenuItem value="">Bitte wählen</MenuItem>
                    {customerMedia.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.filename} ({m.mimeType})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDButton variant="gradient" color="info" fullWidth onClick={addSelectedMedia}>
                    Hinzufügen
                  </MDButton>
                </Grid>
              </Grid>

              <Divider style={{ margin: "16px 0" }} />

              {loopItems.length === 0 ? (
                <MDTypography variant="button" color="text">
                  Keine Elemente im Loop.
                </MDTypography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Medium</TableCell>
                        <TableCell>Dauer (Sek.)</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loopItems.map((it, idx) => {
                        const media = it.media || customerMedia.find((m) => m.id === it.mediaId);
                        return (
                          <TableRow key={it.id || `${it.mediaId}-${idx}`}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{media?.filename || it.mediaId || "-"}</TableCell>
                            <TableCell>
                              {isImage(media?.mimeType) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 1, step: 1 }}
                                  value={it.durationSeconds ?? ""}
                                  onChange={(e) => setDuration(idx, e.target.value)}
                                  style={{ width: 120 }}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Nach oben">
                                <span>
                                  <IconButton size="small" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                                    ↑
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Nach unten">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveItem(idx, +1)}
                                    disabled={idx === loopItems.length - 1}
                                  >
                                    ↓
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Entfernen">
                                <IconButton size="small" onClick={() => removeItem(idx)}>
                                  ✕
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setLoopOpen(false)}>
            Abbrechen
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={saveLoop}
            disabled={savingLoop || loopLoading}
          >
            {savingLoop ? "Speichert…" : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
